import { HttpClient } from '@angular/common/http';
import { inject, Injectable, isDevMode, OnDestroy, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';

import { Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, switchMap, tap, throttleTime } from 'rxjs/operators';

import { AuthState } from '@okta/okta-auth-js';
import { UserResponse } from '@ph-model/login';
import { DavinciTokenRequest } from '@ph-model/request/davinci-token.request';
import { ConfigStore, ConfigStoreType } from '@ph-store/config/config.store';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';

import { LoggerContext } from '../../enums/logger-context.enum';
import { ErrorService } from '../error/error.service';
import { LoggerService } from '../logger/logger.service';
import { ConfigService } from '../config/config.service';
import { OktaService } from '../okta/okta.service';
import { RouterService } from '../router/router.service';
import { AuthFlowTraceService } from './auth-flow-trace.service';
import { InactivityTimeoutService } from './inactivity-timeout.service';
import {
  clearCallbackDeferredToLogin,
  clearIdpSessionPreexisted,
  clearPostLogoutAll,
  clearSsoPhase,
  clearSsoTabState,
  isSilentSsoInFlight,
  isSilentSsoTerminal,
  markCallbackDeferredToLogin,
  markIdpSessionPreexisted,
  markOktaTokensAcquired,
  markPostLogoutAll,
  markSilentSsoFailed,
  markSilentSsoInFlight,
  readCallbackDeferredToLogin,
  readDavinciSsoDefer,
  readPostLogoutAll,
  readSsoPhase,
} from './auth-sso-defer';
import { DavinciSsoAttemptResult, SilentSsoFailureReason, SsoPhase } from './auth-sso-attempt.model';
import { SloLogoutService } from './slo-logout.service';
import {
  AuthDiagnosticScenario,
  SessionAuthenticationDiagnostics,
} from './session-authentication-diagnostics.model';
import { DavinciSessionEvaluation } from './token-inspection.model';
import { TokenService } from './token.service';

interface RefreshTokenResponse {
  token: string;
}

@Injectable({ providedIn: 'root' })
/**
 * Authentication/session service.
 * Owns login-state checks and token/session lifecycle across Okta and Davinci.
 * Authorization decisions (menus, dealer access, feature permissions) belong in guards/stores.
 */
export class AuthService implements OnDestroy {
  oktaAccessToken: WritableSignal<string | null> = signal(null);
  oktaRefreshToken: WritableSignal<string | null> = signal(null);

  private userStore: UserStoreType = inject(UserStore);
  private configStore: ConfigStoreType = inject(ConfigStore);
  private configService = inject(ConfigService);
  private httpClient = inject(HttpClient);
  private oktaService = inject(OktaService);
  private tokenService = inject(TokenService);
  private authFlowTrace = inject(AuthFlowTraceService);
  private errorService = inject(ErrorService);
  private inactivityTimeoutService = inject(InactivityTimeoutService);
  private logger = inject(LoggerService);
  private router = inject(Router);
  private routerService = inject(RouterService);
  private environment = this.configService.environment;
  private tokenTimerSubscription: Subscription | undefined;
  private appVersionSubscription: Subscription | undefined;
  private tokenApiCallSubscription: Subscription | undefined;
  private _tokenRefresh$ = new Subject<void>();
  private _tokenRefreshSubscription: Subscription;
  private _hasRefreshedOktaTokenOnStartup = false;
  private sloLogoutService = inject(SloLogoutService);

  constructor() {
    this._tokenRefreshSubscription = this._tokenRefresh$
      .pipe(
        throttleTime(15000, undefined, { leading: true, trailing: false }),
        switchMap(() =>
          this.httpClient.get<RefreshTokenResponse>(`${this.environment.apiUrl}/refreshtoken`).pipe(
            catchError((error) => throwError(() => error)),
            tap((data: RefreshTokenResponse) => {
              this.tokenService.setToken(data.token);
              this._scheduleNextRenewal(data.token);
            })
          )
        )
      )
      .subscribe();

    this.sloLogoutService.initCrossTabLogoutListener();

    // Same-tab cross-portal: returning to this origin after End all elsewhere leaves stale
    // local tokens; visibility probe never fires on cold navigation — check once at boot.
    if (this.isAuthenticationMechanismOkta() && this.userStore.isLoggedIn()) {
      void this.sloLogoutService.probeIdpSessionOnVisible();
    }
  }

  startInactivityTimer(): void {
    if (!this.inactivityTimeoutService.isEnabled()) {
      return;
    }

    this.inactivityTimeoutService.startMonitoring(() => {
      this.cancelAllSubscriptions();
      this.userStore.logout({ davinciLogout: true, oktaLogout: false });
    });
  }

  startSessionTimer(): void {
    if (!this.inactivityTimeoutService.isMonitoring()) {
      this.startInactivityTimer();
    }
    if (this.isAuthenticationMechanismOkta()) {
      void this.oktaService.captureBoundIdpSession();
    }
  }

  cancelSessionTimer(): void {
    this.inactivityTimeoutService.stopMonitoring();
  }

  isAuthenticationMechanismOkta(): boolean {
    const disableOkta = localStorage.getItem('disable-okta');

    return (
      this.environment.auth.sso && (!isDevMode() || !disableOkta || disableOkta === 'false')
    );
  }

  isAuthenticated(): Promise<boolean> {
    if (this.isAuthenticationMechanismOkta()) {
      return Promise.all([this.isOktaAuthenticated(), this.isDavinciAuthenticated()])
        .then(([isOktaAuthenticated, isDavinciAuthenticated]) => isOktaAuthenticated && isDavinciAuthenticated)
        .catch(() => false);
    }

    return this.isDavinciAuthenticated().catch(() => false);
  }

  /** Capture IdP cookie right before this tab acquires tokens; recomputed so a stale origin-wide flag cannot survive. */
  async capturePreexistingIdpSession(): Promise<void> {
    if (await this.oktaService.hasIdpSession()) {
      markIdpSessionPreexisted(this.environment.application);
    } else {
      clearIdpSessionPreexisted();
    }
  }

  markCallbackDeferredToLoginThisTab(): void {
    markCallbackDeferredToLogin(this.environment.application);
  }

  clearCallbackDeferredToLoginThisTab(): void {
    clearCallbackDeferredToLogin();
  }

  isCallbackDeferredToLoginThisTab(): boolean {
    return readCallbackDeferredToLogin(this.environment.application);
  }

  hasOktaAccessTokenReady(): boolean {
    return !!this.oktaService.getOktaAuth()?.getAccessToken();
  }

  readSsoPhaseThisTab(): SsoPhase {
    return readSsoPhase(this.environment.application);
  }

  isSilentSsoInFlightThisTab(): boolean {
    return isSilentSsoInFlight(this.environment.application);
  }

  isSilentSsoTerminalThisTab(): boolean {
    return isSilentSsoTerminal(this.environment.application);
  }

  markSilentSsoInFlightThisTab(): void {
    markSilentSsoInFlight(this.environment.application);
  }

  markSilentSsoFailedThisTab(failureReason: SilentSsoFailureReason): void {
    markSilentSsoFailed(this.environment.application, failureReason);
  }

  markOktaTokensAcquiredThisTab(): void {
    markOktaTokensAcquired(this.environment.application);
    void this.oktaService.captureBoundIdpSession();
  }

  clearSsoPhaseThisTab(): void {
    clearSsoPhase();
  }

  isPostLogoutAllThisTab(): boolean {
    return readPostLogoutAll(this.environment.application);
  }

  clearPostLogoutAllThisTab(): void {
    clearPostLogoutAll();
  }

  /** Set post-logout flag, stop SSO probes, then chain or Okta sign-out. */
  prepareForLogoutAll(options: { preserveOktaTokens?: boolean; hideError?: boolean } = {}): void {
    this.cancelAllSubscriptions();
    markPostLogoutAll(this.environment.application);

    if (options.hideError) {
      this.errorService.hideErrorMessage();
    }

    clearSsoTabState();
    markSilentSsoFailed(this.environment.application, 'login_required');
    markCallbackDeferredToLogin(this.environment.application);

    if (!options.preserveOktaTokens) {
      this.oktaService.clearLocalOktaTokens();
    }
  }

  /**
   * Direct `/login` visits must run silent SSO on `/callback` once before showing the widget.
   * After callback defers or silent SSO fails, this returns false to avoid login↔callback loops.
   */
  shouldAttemptSilentSsoViaCallback(): boolean {
    if (!this.isAuthenticationMechanismOkta()) {
      return false;
    }
    if (this.isPostLogoutAllThisTab()) {
      return false;
    }
    if (this.hasOktaAccessTokenReady()) {
      return false;
    }
    if (this.isCallbackDeferredToLoginThisTab()) {
      return false;
    }

    return this.readSsoPhaseThisTab() === 'none';
  }

  /** Re-evaluate and route to error page when blocked. Returns true when navigation occurred. */
  async tryEvaluateAndBlockSso(context: string): Promise<boolean> {
    const attempt = await this.evaluateDavinciSsoAttempt(context);
    return this.handleBlockedSsoAttempt(attempt, context);
  }

  /**
   * Routes to the SSO error page when the attempt is blocked. Returns true when the caller should stop.
   */
  handleBlockedSsoAttempt(attempt: DavinciSsoAttemptResult, context: string): boolean {
    if (attempt.defer) {
      clearSsoPhase();
      this.logger.warn(LoggerContext.Auth, 'Deferring Davinci SSO — prior classified failure in this tab', {
        context,
        defer: attempt.defer,
      });
      this.errorService.displaySsoError(attempt.defer.scenario, attempt.defer.message);
      void this.router.navigate(['/callback/error']);
      return true;
    }

    return false;
  }

  /**
   * Whether to call `performSsoDavinciLogin` now. Defers repeat attempts after a classified failure
   * in this tab. Partial Okta-without-Davinci sessions proceed so Davinci can authorize the portal.
   */
  async evaluateDavinciSsoAttempt(context: string, targetUrl?: string): Promise<DavinciSsoAttemptResult> {
    if (!this.isAuthenticationMechanismOkta()) {
      return {};
    }

    const application = this.environment.application;
    if (readPostLogoutAll(application)) {
      this.authFlowTrace.record('unknown', `${context}:sso-skipped-post-logout-all`, { application }, targetUrl ?? null);
      return {};
    }
    const diagnostics = await this.getSessionAuthenticationDiagnostics(context, targetUrl);

    const defer = readDavinciSsoDefer(application);
    if (defer) {
      this.authFlowTrace.record('unknown', `${context}:sso-deferred`, { ...defer }, targetUrl ?? null);

      return { defer };
    }

    // ponytail: universal user — same Okta across portals; getOktaUser authorizes per applicationId.
    if (diagnostics.oktaSessionPresentRaw && !diagnostics.isDavinciAuthenticated) {
      const detail = {
        context,
        application,
        davinciAuthFailureReason: diagnostics.davinciAuthFailureReason,
        userStoreIsLoggedIn: diagnostics.userStoreIsLoggedIn,
        locationHref: diagnostics.locationHref,
      };
      this.logger.warn(
        LoggerContext.Auth,
        `Partial portal session on "${application}": Okta session is active but this portal has no Davinci session (${diagnostics.davinciAuthFailureReason ?? 'unknown'}). Proceeding with getOktaUser probe.`,
        detail
      );
      this.authFlowTrace.record('unknown', `${context}:partial-portal-session`, detail, targetUrl ?? null);
    }

    return {};
  }

  /**
   * Read-only session snapshot for debugging. Does not refresh Okta or run Davinci SSO.
   * Use `isAuthenticated()` for access decisions.
   */
  getSessionAuthenticationDiagnostics(
    context: string,
    targetUrl?: string
  ): Promise<SessionAuthenticationDiagnostics> {
    const isAuthenticationMechanismOkta = this.isAuthenticationMechanismOkta();
    const scenario = this._resolveDiagnosticScenario(context, targetUrl);
    const persistedUserStore = this._readPersistedUserStoreSnapshot();
    const oktaAuth = this.oktaService.getOktaAuth();
    const oktaSessionPresentRawPromise = oktaAuth?.isAuthenticated() ?? Promise.resolve(false);
    const davinciSession = this._evaluateDavinciSessionState();

    return oktaSessionPresentRawPromise
      .then((oktaSessionPresentRaw) => {
        const isDavinciAuthenticated = davinciSession.isAuthenticated;
        const isOktaAuthenticated = oktaSessionPresentRaw;
        const isAuthenticated = isAuthenticationMechanismOkta
          ? isOktaAuthenticated && isDavinciAuthenticated
          : isDavinciAuthenticated;

        const diagnostics = this._buildSessionAuthenticationDiagnostics({
          context,
          scenario,
          targetUrl,
          isAuthenticationMechanismOkta,
          isAuthenticated,
          isOktaAuthenticated,
          isDavinciAuthenticated,
          oktaSessionPresentRaw,
          davinciSession,
          persistedUserStore,
        });

        this.authFlowTrace.record(scenario, `${context}:snapshot`, {
          isAuthenticated: diagnostics.isAuthenticated,
          oktaSessionPresentRaw,
          davinciFailure: davinciSession.failureReason,
          tokenSuffix: davinciSession.token?.tokenSuffix,
          msUntilExpiry: davinciSession.token?.msUntilExpiry,
          userStoreIsLoggedIn: davinciSession.isLoggedIn,
          persistedUserStoreIsLoggedIn: persistedUserStore.isLoggedIn,
          hasRefreshedOktaTokenOnStartup: diagnostics.hasRefreshedOktaTokenOnStartup,
        }, targetUrl ?? null);

        return diagnostics;
      })
      .catch((error: unknown) => {
        const davinciAfterError = this._evaluateDavinciSessionState();
        this.authFlowTrace.record(scenario, `${context}:diagnostics-error`, {
          error: error instanceof Error ? error.message : String(error),
        }, targetUrl ?? null);

        return this._buildSessionAuthenticationDiagnostics({
          context,
          scenario,
          targetUrl,
          isAuthenticationMechanismOkta,
          isAuthenticated: false,
          isOktaAuthenticated: false,
          isDavinciAuthenticated: false,
          oktaSessionPresentRaw: false,
          davinciSession: davinciAfterError,
          persistedUserStore,
        });
      });
  }

  /**
   * When `refreshOktaTokenOnStartup` is enabled, performs a single Okta access-token renew and
   * marks startup refresh as done so `isOktaAuthenticated` will not run a second refresh + SSO.
   * Call from the Okta callback before the initial `/okta/ssologin` so that request uses the fresh token.
   */
  async ensureStartupOktaTokenRefreshBeforeInitialSso(): Promise<void> {
    if (!this.environment.auth?.refreshOktaTokenOnStartup || this._hasRefreshedOktaTokenOnStartup) {
      return;
    }
    this._hasRefreshedOktaTokenOnStartup = true;
    await this.oktaService.refreshAccessToken().catch(() => {
      console.warn('Failed to refresh Okta token - reusing existing token instead');
    });
  }

  isOktaAuthenticated(): Promise<boolean> {
    const oktaAuth = this.oktaService.getOktaAuth();
    if (!oktaAuth) {
      return Promise.resolve(false);
    }
    return oktaAuth.isAuthenticated().then(async (isOktaAuthenticated) => {
      if (isOktaAuthenticated) {
        const shouldRefreshOnStartup =
          this.environment.auth?.refreshOktaTokenOnStartup &&
          !this._hasRefreshedOktaTokenOnStartup &&
          !readPostLogoutAll(this.environment.application);
        if (shouldRefreshOnStartup) {
          this._hasRefreshedOktaTokenOnStartup = true;
          await this.refreshAccessTokens().catch(() => {
            console.warn('Failed to refresh Okta token - reusing existing token instead');
          });
        }
        this.oktaAccessToken.set(oktaAuth.getAccessToken() ?? null);
        this.oktaRefreshToken.set(oktaAuth.getRefreshToken() ?? null);
      } else {
        this.oktaAccessToken.set(null);
        this.oktaRefreshToken.set(null);
      }

      return isOktaAuthenticated;
    }).catch(() => false);
  }

  isDavinciAuthenticated(): Promise<boolean> {
    return Promise.resolve(this._evaluateDavinciSessionState().isAuthenticated);
  }

  private _evaluateDavinciSessionState(): DavinciSessionEvaluation {
    const isLoggedIn = this.userStore.isLoggedIn();
    const token = this.tokenService.getToken();
    const originalUserToken = this.tokenService.getOriginalUserToken() ?? null;
    const tokenInspection = this.tokenService.inspectToken(token);
    const originalUserTokenInspection = this.tokenService.inspectToken(originalUserToken);
    const tokenCookiesDiffer: boolean | null =
      tokenInspection?.tokenSuffix != null && originalUserTokenInspection?.tokenSuffix != null
        ? tokenInspection.tokenSuffix !== originalUserTokenInspection.tokenSuffix
        : null;
    const hasToken = !!token;

    if (isDevMode() && !this.environment.tokenExpirationCheck) {
      return {
        isAuthenticated: isLoggedIn,
        failureReason: isLoggedIn ? null : 'dev-mode-not-logged-in',
        hasToken,
        isLoggedIn,
        tokenExpired: null,
        tokenExpiresAt: null,
        token: tokenInspection,
        originalUserToken: originalUserTokenInspection,
        tokenCookiesDiffer,
      };
    }

    if (!hasToken) {
      return {
        isAuthenticated: false,
        failureReason: 'no-token-cookie',
        hasToken,
        isLoggedIn,
        tokenExpired: null,
        tokenExpiresAt: null,
        token: tokenInspection,
        originalUserToken: originalUserTokenInspection,
        tokenCookiesDiffer,
      };
    }

    if (!isLoggedIn) {
      return {
        isAuthenticated: false,
        failureReason: 'user-store-not-logged-in',
        hasToken,
        isLoggedIn,
        tokenExpired: null,
        tokenExpiresAt: null,
        token: tokenInspection,
        originalUserToken: originalUserTokenInspection,
        tokenCookiesDiffer,
      };
    }

    if (!tokenInspection || tokenInspection.decodeError) {
      return {
        isAuthenticated: false,
        failureReason: 'token-decode-failed',
        hasToken,
        isLoggedIn,
        tokenExpired: null,
        tokenExpiresAt: null,
        token: tokenInspection,
        originalUserToken: originalUserTokenInspection,
        tokenCookiesDiffer,
      };
    }

    if (tokenInspection.expClaimSeconds === null) {
      return {
        isAuthenticated: false,
        failureReason: 'token-missing-exp-claim',
        hasToken,
        isLoggedIn,
        tokenExpired: null,
        tokenExpiresAt: null,
        token: tokenInspection,
        originalUserToken: originalUserTokenInspection,
        tokenCookiesDiffer,
      };
    }

    const tokenExpiresAt = tokenInspection.expiresAtIso;
    const tokenExpired = tokenInspection.clientConsidersExpired;

    return {
      isAuthenticated: !tokenExpired,
      failureReason: tokenExpired ? 'token-expired' : null,
      hasToken,
      isLoggedIn,
      tokenExpired,
      tokenExpiresAt,
      token: tokenInspection,
      originalUserToken: originalUserTokenInspection,
      tokenCookiesDiffer,
    };
  }

  private _buildSessionAuthenticationDiagnostics(input: {
    context: string;
    scenario: AuthDiagnosticScenario;
    targetUrl?: string;
    isAuthenticationMechanismOkta: boolean;
    isAuthenticated: boolean;
    isOktaAuthenticated: boolean;
    isDavinciAuthenticated: boolean;
    oktaSessionPresentRaw: boolean;
    davinciSession: DavinciSessionEvaluation;
    persistedUserStore: { isLoggedIn: boolean | null; hasUser: boolean | null };
  }): SessionAuthenticationDiagnostics {
    const { davinciSession, persistedUserStore } = input;

    return {
      context: input.context,
      scenario: input.scenario,
      traceId: this.authFlowTrace.getTraceId(),
      targetUrl: input.targetUrl ?? null,
      redirectUrl: this._safeReadRedirectUrl(),
      entryNavigationUrl: this._readEntryNavigationUrl(),
      locationHref: typeof window !== 'undefined' ? window.location.href : '',
      isAuthenticationMechanismOkta: input.isAuthenticationMechanismOkta,
      isAuthenticated: input.isAuthenticated,
      isOktaAuthenticated: input.isOktaAuthenticated,
      isDavinciAuthenticated: input.isDavinciAuthenticated,
      oktaSessionPresentRaw: input.oktaSessionPresentRaw,
      userStoreIsLoggedIn: davinciSession.isLoggedIn,
      persistedUserStoreIsLoggedIn: persistedUserStore.isLoggedIn,
      persistedUserStoreHasUser: persistedUserStore.hasUser,
      hasDavinciTokenCookie: davinciSession.hasToken,
      davinciTokenExpired: davinciSession.tokenExpired,
      davinciTokenExpiresAt: davinciSession.tokenExpiresAt,
      davinciAuthFailureReason: davinciSession.failureReason,
      davinciSession,
      refreshOktaTokenOnStartup: this.environment.auth?.refreshOktaTokenOnStartup === true,
      hasRefreshedOktaTokenOnStartup: this._hasRefreshedOktaTokenOnStartup,
      tokenExpirationCheckEnabled: this.environment.tokenExpirationCheck,
      isDevMode: isDevMode(),
    };
  }

  private _resolveDiagnosticScenario(context: string, targetUrl?: string): AuthDiagnosticScenario {
    if (context === 'authGuard' && targetUrl?.startsWith('/home')) {
      return 'guard-on-home-route';
    }
    if (context.startsWith('callback:')) {
      return 'callback-orchestration';
    }
    if (context === 'scheduleTokenRenewal') {
      return 'token-renewal-scheduler';
    }

    return 'unknown';
  }

  private _readPersistedUserStoreSnapshot(): { isLoggedIn: boolean | null; hasUser: boolean | null } {
    try {
      const raw = localStorage.getItem('UserStore');
      if (!raw) {
        return { isLoggedIn: null, hasUser: null };
      }
      const parsed = JSON.parse(raw) as { isLoggedIn?: boolean; user?: unknown };
      return {
        isLoggedIn: typeof parsed.isLoggedIn === 'boolean' ? parsed.isLoggedIn : null,
        hasUser: parsed.user != null,
      };
    } catch {
      return { isLoggedIn: null, hasUser: null };
    }
  }

  private _readEntryNavigationUrl(): string | null {
    try {
      if (typeof performance === 'undefined') {
        return null;
      }
      const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      return entry?.name ?? null;
    } catch {
      return null;
    }
  }

  private _safeReadRedirectUrl(): string | null {
    try {
      return localStorage.getItem('redirectUrl');
    } catch {
      return null;
    }
  }

  refreshAccessTokens(): Promise<{ oktaAccessToken: string; userResponse: UserResponse }> {
    if (readPostLogoutAll(this.environment.application)) {
      return Promise.reject(new Error('logout-all-in-progress'));
    }

    return this.oktaService
      .refreshAccessToken()
      .then((oktaAccessToken) => {
        if (!oktaAccessToken) {
          throw new Error('Failed to refresh Okta access token: no token returned');
        }

        // ponytail: HCI `refreshOktaTokenOnStartup` calls this on F5; tokenRefreshOnly ssologin
        // overwrites emulated user/originalUser/token while leaving emulatedDealer true. Skip Davinci
        // SSO while emulating — Okta renew still runs. Other portals keep prior behavior (flag off).
        if (this.userStore.emulatedDealer()) {
          const userResponse = this.userStore.user();
          if (!userResponse) {
            throw new Error('Cannot skip Davinci refresh: emulated session has no user in store');
          }
          this.logger.log(LoggerContext.Auth, 'Skipping Davinci token refresh — dealer emulation active');
          return { oktaAccessToken, userResponse };
        }

        const clientConfig = this.configStore.clientConfig();
        const programId = clientConfig?.programId ?? null;
        const applicationId = clientConfig?.applicationId ?? null;
        const application = this.environment.application;

        if (!programId || !applicationId || !application) {
          throw new Error(
            'Cannot refresh Davinci token: missing portal configuration (programId, applicationId, or application)'
          );
        }

        const davinciTokenRequest: DavinciTokenRequest = {
          programId,
          applicationId,
          application,
          oktaAccessToken,
          oktaUserUid: 'to-be-removed',
        };

        return this.userStore
          .performSsoDavinciLogin(davinciTokenRequest, { tokenRefreshOnly: true })
          .then((userResponse) => ({ oktaAccessToken, userResponse }))
          .catch((err) => {
            throw new Error(
              `Davinci token refresh failed after Okta refresh: ${err instanceof Error ? err.message : String(err)}`
            );
          });
      })
      .catch((err) => {
        if (err instanceof Error && err.message.includes('Davinci')) {
          throw err;
        }
        if (err instanceof Error) {
          throw new Error(`Failed to refresh Okta access token: ${err.message}`);
        }

        throw new Error('Failed to refresh Okta access token');
      });
  }

  scheduleTokenRenewal(): void {
    if (isDevMode() && !this.environment.tokenExpirationCheck) {
      return;
    }
    this.cancelTokenRenewal();

    this.isAuthenticated().then((isAuthenticated) => {
      if (!isAuthenticated) {
        void this.getSessionAuthenticationDiagnostics('scheduleTokenRenewal').then((diagnostics) => {
          this.authFlowTrace.record('token-renewal-scheduler', 'scheduleTokenRenewal:logout', {
            davinciFailure: diagnostics.davinciAuthFailureReason,
            msUntilExpiry: diagnostics.davinciSession.token?.msUntilExpiry,
          });
        });
        this.cancelAllSubscriptions();
        this.userStore.logout({ davinciLogout: true, oktaLogout: false });

        return;
      }

      const token = this.tokenService.getToken();
      if (token) {
        this._scheduleNextRenewal(token);
      }
    });
  }

  cancelTokenRenewal(): void {
    this.tokenTimerSubscription?.unsubscribe();
  }

  cancelAllSubscriptions(): void {
    this.tokenTimerSubscription?.unsubscribe();
    this.inactivityTimeoutService.stopMonitoring();
  }

  ngOnDestroy(): void {
    this.tokenTimerSubscription?.unsubscribe();
    this.appVersionSubscription?.unsubscribe();
    this.tokenApiCallSubscription?.unsubscribe();
    this._tokenRefreshSubscription?.unsubscribe();
    this._tokenRefresh$.complete();
    this.inactivityTimeoutService.stopMonitoring();
  }

  private _scheduleNextRenewal(token: string): void {
    let tokenExpiration: Date | null = null;

    try {
      tokenExpiration = this.tokenService.getExpirationDate(token);
    } catch (error) {
      this.tokenService.deleteToken();
      return;
    }
    
    if (!tokenExpiration) {
      return;
    }
    const now = Date.now();
    const timeBeforeExpirationToStartRenewing = 2 * 60 * 1000;

    this.tokenTimerSubscription?.unsubscribe();
    this.tokenTimerSubscription = timer(
      Math.max(1, tokenExpiration.getTime() - timeBeforeExpirationToStartRenewing - now)
    ).subscribe(() => {
      if (this.environment.tokenExpirationCheck) {
        this._tokenRefresh$.next();
      }
    });
  }
}
